import { useCallback, useRef, useState } from 'react';
import { ApolloCache, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  CompanyContractDraftInput,
  EsignContractPdfLinksDocument,
  EsignContractPdfLinksQuery,
  EsignContractPdfLinksQueryVariables,
  StudentContractDraftInput,
  useCancelEsignContractMutation,
  useCreateCompanyContractMutation,
  useCreateStudentContractMutation,
  useCreateTeacherAnnexMutation,
  useCreateTeacherFrameworkMutation,
  useReassignEsignSellerMutation,
  useResendEsignEmailMutation,
  useRetryEsignFulfilmentMutation,
  useRevealEsignLinkMutation,
  useRevealEsignShortLinkMutation,
  useSendEsignContractMutation,
  useUpdateCompanyContractMutation,
  useUpdateStudentContractMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { copyProducedText } from './clipboard';
import { IContractDocumentCard } from './useContractDocuments';

export interface IEsignFieldError {
  field: string;
  code: string;
  message: string;
}

export interface IEsignMutationResult {
  ok: boolean;
  contract: IContractDocumentCard | null;
  errors: IEsignFieldError[];
}

type RawErrors =
  | Array<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

interface IPayload {
  success?: boolean | null;
  customErrors?: RawErrors;
  contract?: IContractDocumentCard | null;
}

export const toEsignErrors = (raw: RawErrors): IEsignFieldError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));

/**
 * Список договоров — поле с аргументами на ROOT_QUERY. После создания
 * документа выбрасываем его из кэша целиком: каждый активный список
 * (страница, карточка студента, карточка преподавателя) перечитает себя сам.
 *
 * Не `refetchQueries` по имени операции: так бьётся и по пропущенному
 * (`skip`) запросу, а это 400 и «/500» (грабли `refetch-by-name-skipped-query`).
 */
const evictContractLists = (cache: ApolloCache<unknown>) => {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'contractDocuments' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'contractDocumentsTotal' });
  cache.gc();
};

/**
 * Смена продавца (ТЗ 0078) меняет выдачу списка с фильтром по продавцу и
 * набор вариантов самого фильтра — оба поля перечитываются.
 */
const evictSellerViews = (cache: ApolloCache<unknown>) => {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'contractSellers' });
  evictContractLists(cache);
};

/** ТЗ 0094: разовая правка письма-приглашения при отправке договора. */
export interface IEsignSendOptions {
  emailSubject?: string | null;
  emailBody?: string | null;
  silent?: boolean;
}

/** Какой PDF открыть: черновой (что ушло на подпись) или подписанный. */
export type EsignPdfKind = 'preview' | 'signed';

/**
 * ТЗ 0076 — все действия над договорами на подпись.
 *
 * Два разных класса отказа:
 * * бизнес-ошибка приходит в `customErrors` (промис успешен) — её код
 *   переводится `esign.errors.<CODE>`, а неизвестный показывается текстом
 *   бэкенда;
 * * отказ прав — это GraphQL `errors`, промис ОТКЛОНЯЕТСЯ; без try/catch это
 *   необработанный reject и ноль обратной связи.
 *
 * Тост поднимается ВНУТРИ хука — уронить его на пол вызывающий не может.
 * Исключение — создание и правка черновика: там ошибки полей рисует форма, и
 * второй тост с тем же текстом был бы эхом.
 */
export const useEsignMutations = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const client = useApolloClient();

  const [
    createDraft,
    { loading: creating },
  ] = useCreateStudentContractMutation({ update: evictContractLists });
  const [
    updateDraft,
    { loading: updating },
  ] = useUpdateStudentContractMutation();
  const [
    createCompany,
    { loading: creatingCompany },
  ] = useCreateCompanyContractMutation({ update: evictContractLists });
  const [
    updateCompany,
    { loading: updatingCompany },
  ] = useUpdateCompanyContractMutation();
  const [sendContract, { loading: sending }] = useSendEsignContractMutation();
  const [resendEmail, { loading: resending }] = useResendEsignEmailMutation();
  const [revealLink] = useRevealEsignLinkMutation({ fetchPolicy: 'no-cache' });
  const [revealShortLink] = useRevealEsignShortLinkMutation({
    fetchPolicy: 'no-cache',
  });
  const [
    reassignContractSeller,
    { loading: reassigning },
  ] = useReassignEsignSellerMutation({ update: evictSellerViews });
  const [
    cancelContract,
    { loading: cancelling },
  ] = useCancelEsignContractMutation();
  const [
    retryFulfilment,
    { loading: retrying },
  ] = useRetryEsignFulfilmentMutation();
  const [
    createFramework,
    { loading: creatingFramework },
  ] = useCreateTeacherFrameworkMutation({ update: evictContractLists });
  const [
    createAnnex,
    { loading: creatingAnnex },
  ] = useCreateTeacherAnnexMutation({ update: evictContractLists });

  const [copying, setCopying] = useState(false);
  const copyingRef = useRef(false);
  const [openingPdf, setOpeningPdf] = useState(false);

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Esign,
      });
    },
    [addToast],
  );

  const errorText = useCallback(
    (errors: IEsignFieldError[]): string =>
      errors
        .map((error) =>
          error.code
            ? t(`esign.errors.${error.code}`, {
                defaultValue: error.message || error.code,
              })
            : error.message,
        )
        .filter(Boolean)
        .join('\n'),
    [t],
  );

  /** Разбор ответа мутации + тост. `silent` — ошибки рисует форма. */
  const settle = useCallback(
    (
      payload: IPayload | null | undefined,
      successKey: string | null,
      silent = false,
    ): IEsignMutationResult => {
      const errors = toEsignErrors(payload?.customErrors);
      if (payload?.success) {
        if (successKey) notify(ToastTypes.success, t(successKey));
        return { ok: true, contract: payload.contract ?? null, errors };
      }
      if (!silent) {
        notify(ToastTypes.error, t('esign.toast.failed'), errorText(errors));
      }
      return { ok: false, contract: payload?.contract ?? null, errors };
    },
    [errorText, notify, t],
  );

  const crash = useCallback(
    (error: unknown, silent = false): IEsignMutationResult => {
      const message = error instanceof Error ? error.message : '';
      if (!silent) {
        notify(ToastTypes.error, t('esign.toast.failed'), message || undefined);
        return { ok: false, contract: null, errors: [] };
      }
      // Тихий режим: ошибку покажет само окно (вложенное окно выше
      // уведомлений приложения).
      return {
        ok: false,
        contract: null,
        errors: [{ field: '', code: '', message }],
      };
    },
    [notify, t],
  );

  const createStudentDraft = useCallback(
    async (input: StudentContractDraftInput) => {
      try {
        const { data } = await createDraft({ variables: { input } });
        return settle(
          data?.createStudentContractDraft,
          'esign.toast.created',
          true,
        );
      } catch (error) {
        return crash(error);
      }
    },
    [createDraft, crash, settle],
  );

  const updateStudentDraft = useCallback(
    async (id: string, input: StudentContractDraftInput) => {
      try {
        const { data } = await updateDraft({ variables: { id, input } });
        return settle(data?.updateContractDraft, 'esign.toast.updated', true);
      } catch (error) {
        return crash(error);
      }
    },
    [updateDraft, crash, settle],
  );

  const createCompanyDraft = useCallback(
    async (input: CompanyContractDraftInput) => {
      try {
        const { data } = await createCompany({ variables: { input } });
        return settle(
          data?.createCompanyContractDraft,
          'esign.toast.created',
          true,
        );
      } catch (error) {
        return crash(error);
      }
    },
    [createCompany, crash, settle],
  );

  const updateCompanyDraft = useCallback(
    async (id: string, input: CompanyContractDraftInput) => {
      try {
        const { data } = await updateCompany({ variables: { id, input } });
        return settle(
          data?.updateCompanyContractDraft,
          'esign.toast.updated',
          true,
        );
      } catch (error) {
        return crash(error);
      }
    },
    [updateCompany, crash, settle],
  );

  /**
   * ТЗ 0094: `emailSubject` / `emailBody` — разовая правка письма-
   * приглашения (сырой текст с `{…}`); не переданы — текст шаблона, как
   * раньше. `silent` — отказ рисует окно отправки у своих полей.
   */
  const send = useCallback(
    async (
      id: string,
      sendEmail: boolean,
      { emailSubject, emailBody, silent = false }: IEsignSendOptions = {},
    ) => {
      try {
        const { data } = await sendContract({
          variables: {
            id,
            sendEmail,
            ...(emailSubject ? { emailSubject } : {}),
            ...(emailBody ? { emailBody } : {}),
          },
        });
        return settle(
          data?.sendContract,
          sendEmail ? 'esign.toast.sendingEmail' : 'esign.toast.sendingLink',
          silent,
        );
      } catch (error) {
        return crash(error, silent);
      }
    },
    [sendContract, crash, settle],
  );

  /** ТЗ 0094: та же разовая правка, что у `send`. */
  const resend = useCallback(
    async (
      id: string,
      { emailSubject, emailBody, silent = false }: IEsignSendOptions = {},
    ) => {
      try {
        const { data } = await resendEmail({
          variables: {
            id,
            ...(emailSubject ? { emailSubject } : {}),
            ...(emailBody ? { emailBody } : {}),
          },
        });
        return settle(data?.resendContractEmail, 'esign.toast.resent', silent);
      } catch (error) {
        return crash(error, silent);
      }
    },
    [resendEmail, crash, settle],
  );

  const cancel = useCallback(
    async (id: string, reason: string, silent = false) => {
      try {
        const { data } = await cancelContract({ variables: { id, reason } });
        return settle(data?.cancelContract, 'esign.toast.cancelled', silent);
      } catch (error) {
        return crash(error, silent);
      }
    },
    [cancelContract, crash, settle],
  );

  const retry = useCallback(
    async (id: string, existingUserId?: string | null) => {
      try {
        const { data } = await retryFulfilment({
          variables: { id, existingUserId: existingUserId || null },
        });
        return settle(data?.retryContractFulfilment, 'esign.toast.retried');
      } catch (error) {
        return crash(error);
      }
    },
    [retryFulfilment, crash, settle],
  );

  /**
   * Рамочный договор преподавателя. `silent` — отказ рисует само окно
   * («Договор с преподавателем» на странице договоров) рядом с кнопкой, и
   * тост лишь повторил бы тот же текст. Успех тостом — всегда.
   */
  const createTeacherFramework = useCallback(
    async (teacherId: string, templateCode?: string | null, silent = false) => {
      try {
        const { data } = await createFramework({
          variables: { teacherId, templateCode: templateCode || null },
        });
        return settle(
          data?.createTeacherFrameworkDraft,
          'esign.toast.teacherDraftCreated',
          silent,
        );
      } catch (error) {
        return crash(error, silent);
      }
    },
    [createFramework, crash, settle],
  );

  const createTeacherAnnex = useCallback(
    async (applicationId: string) => {
      try {
        const { data } = await createAnnex({ variables: { applicationId } });
        return settle(
          data?.createTeacherAnnexDraft,
          'esign.toast.teacherDraftCreated',
        );
      } catch (error) {
        return crash(error);
      }
    },
    [createAnnex, crash, settle],
  );

  /**
   * 🔴 Ссылка на подпись (длинная или короткая) — bearer-секрет.
   *
   * Мутация раскрытия зовётся с `no-cache`: `network-only` всё равно пишет
   * ответ в кэш (грабли `network-only-still-writes-cache`). Строка уходит
   * прямиком в буфер обмена — не в состояние, не в DOM, не в лог. Каждое
   * раскрытие бэкенд пишет в журнал договора.
   *
   * Защёлка в ref, а не в state: второй клик в том же такте ещё видит старое
   * состояние и раскрыл бы ссылку дважды (две записи в журнале). Защёлка
   * ОДНА на обе ссылки — за один клик раскрывается одна.
   */
  const copySecret = useCallback(
    async (
      id: string,
      reveal: () => Promise<
        | {
            success?: boolean | null;
            url?: string | null;
            customErrors?: RawErrors;
          }
        | null
        | undefined
      >,
      keys: { copied: string; failed: string },
    ): Promise<boolean> => {
      if (copyingRef.current) return false;
      copyingRef.current = true;
      setCopying(true);
      let refused = false;
      try {
        const outcome = await copyProducedText(async () => {
          try {
            const payload = await reveal();
            if (payload?.success && payload.url) return payload.url;
            refused = true;
            notify(
              ToastTypes.error,
              t(keys.failed),
              errorText(toEsignErrors(payload?.customErrors)),
            );
            return null;
          } catch (error) {
            refused = true;
            crash(error);
            return null;
          }
        });
        if (outcome === 'copied') {
          notify(ToastTypes.success, t(keys.copied));
          // Раскрытие ссылки бэкенд пишет в журнал договора. Сбрасываем
          // только `events` карточки — открытое окно дочитает хронологию с
          // сервера и покажет, кто взял ссылку. Сама ссылка в кэш не идёт.
          client.cache.evict({
            id: client.cache.identify({
              __typename: 'ContractDocumentType',
              id,
            }),
            fieldName: 'events',
          });
          return true;
        }
        if (!refused) {
          notify(
            ToastTypes.error,
            t(keys.failed),
            t('esign.toast.clipboardDenied'),
          );
        }
        return false;
      } finally {
        copyingRef.current = false;
        setCopying(false);
      }
    },
    [client, crash, errorText, notify, t],
  );

  /** Длинная ссылка Documenso (`revealContractSigningLink`). */
  const copyLink = useCallback(
    (id: string): Promise<boolean> =>
      copySecret(
        id,
        async () =>
          (await revealLink({ variables: { id } })).data
            ?.revealContractSigningLink,
        { copied: 'esign.toast.linkCopied', failed: 'esign.toast.linkFailed' },
      ),
    [copySecret, revealLink],
  );

  /**
   * Короткая ссылка (ТЗ 0078) — та же, что уходит в SMS и в письме: переходы
   * по ней считаются в карточке. Только договор студента в `sent`/`opened`;
   * без настроенного сократителя бэкенд отвечает `SHORTLINK_DISABLED`, и тост
   * советует взять обычную ссылку.
   */
  const copyShortLink = useCallback(
    (id: string): Promise<boolean> =>
      copySecret(
        id,
        async () =>
          (await revealShortLink({ variables: { contractId: id } })).data
            ?.revealContractShortLink,
        {
          copied: 'esign.toast.shortLinkCopied',
          failed: 'esign.toast.shortLinkFailed',
        },
      ),
    [copySecret, revealShortLink],
  );

  /**
   * Скопировать обычный текст (email, телефон подписанта) — со строки
   * таблицы. Не секрет, поэтому без журнала и без защёлки.
   */
  const copyText = useCallback(
    async (text: string): Promise<boolean> => {
      const outcome = await copyProducedText(async () => text || null);
      if (outcome === 'copied') {
        notify(ToastTypes.success, t('esign.toast.copied'));
        return true;
      }
      notify(ToastTypes.error, t('esign.toast.copyFailed'));
      return false;
    },
    [notify, t],
  );

  /**
   * Сменить продавца (ТЗ 0078, только Admin). Ошибки полей (`REASON_REQUIRED`,
   * `INVALID_SELLER`, `SAME_SELLER`) рисует окно смены — тоста с тем же
   * текстом не поднимаем.
   */
  const reassignSeller = useCallback(
    async (id: string, sellerId: string, reason: string) => {
      try {
        const { data } = await reassignContractSeller({
          variables: { id, sellerId, reason },
        });
        return settle(
          data?.reassignContractSeller,
          'esign.toast.sellerChanged',
          true,
        );
      } catch (error) {
        // Диалог смены продавца — вложенное окно: уведомление ушло бы под
        // его затемнение, ошибку показывает сам диалог.
        return crash(error, true);
      }
    },
    [reassignContractSeller, crash, settle],
  );

  /**
   * Открыть PDF в новой вкладке.
   *
   * Presigned-ссылка живёт 5 минут, поэтому перечитывается в момент клика
   * (`no-cache`), а не берётся из открытой полчаса назад карточки. Вкладка
   * открывается СИНХРОННО, до запроса: иначе блокировщик всплывающих окон
   * съест `window.open` после ответа сервера (приём `useGroupDiplomas`).
   */
  const openPdf = useCallback(
    async (id: string, kind: EsignPdfKind): Promise<void> => {
      const tab = window.open('', '_blank');
      setOpeningPdf(true);
      try {
        const { data } = await client.query<
          EsignContractPdfLinksQuery,
          EsignContractPdfLinksQueryVariables
        >({
          query: EsignContractPdfLinksDocument,
          variables: { id },
          fetchPolicy: 'no-cache',
        });
        const url =
          kind === 'signed'
            ? data?.contractDocument?.signedPdfUrl
            : data?.contractDocument?.previewPdfUrl;
        if (!url) {
          tab?.close();
          notify(ToastTypes.error, t('esign.toast.pdfMissing'));
          return;
        }
        if (tab) {
          // Ссылка — чужой домен (S3): вкладке не нужен доступ к ERP.
          tab.opener = null;
          tab.location.href = url;
        } else {
          notify(ToastTypes.error, t('esign.toast.popupBlocked'));
        }
      } catch (error) {
        tab?.close();
        crash(error);
      } finally {
        setOpeningPdf(false);
      }
    },
    [client, crash, notify, t],
  );

  return {
    createStudentDraft,
    updateStudentDraft,
    createCompanyDraft,
    updateCompanyDraft,
    send,
    resend,
    cancel,
    retry,
    createTeacherFramework,
    createTeacherAnnex,
    copyLink,
    copyShortLink,
    copyText,
    reassignSeller,
    openPdf,
    errorText,
    busy: {
      creating: creating || creatingCompany,
      updating: updating || updatingCompany,
      sending,
      resending,
      cancelling,
      retrying,
      creatingTeacherDraft: creatingFramework || creatingAnnex,
      copying,
      reassigning,
      openingPdf,
    },
  };
};
